import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useApolloClient } from '@apollo/client';
import { ImpressionTrackingService } from './impression-tracking.service';
import type { OfferImpressionTrackingContext } from './tracking.types';

export const useImpressionTracking = (ctx: OfferImpressionTrackingContext | null | undefined) => {
  const client = useApolloClient();

  // Stable DOM node reference used by the click listener effect.
  const nodeRef = useRef<Element | null>(null);

  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
    skip: !ctx,
  });

  // Combined ref: feeds the node into both the intersection observer and nodeRef.
  const ref = useCallback(
    (node: Element | null) => {
      nodeRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  const trackedIdRef = useRef<string | null>(null);

  useEffect(() => {
    ImpressionTrackingService.init(client);
  }, [client]);

  // Reset tracked state when the displayed offer changes.
  useEffect(() => {
    trackedIdRef.current = null;
  }, [ctx?.activateOfferId]);

  // Log impression once per offer when 50 % of the card enters the viewport.
  useEffect(() => {
    if (!inView || !ctx) {
      return;
    }
    if (trackedIdRef.current === ctx.activateOfferId) {
      return;
    }
    trackedIdRef.current = ctx.activateOfferId;
    ImpressionTrackingService.trackImpression(ctx);
  }, [inView, ctx]);

  // Log click via native event so any tap on the card (incl. accessory button) is captured.
  useEffect(() => {
    const node = nodeRef.current;
    if (!ctx || !node) {
      return undefined;
    }
    const handleClick = () => ImpressionTrackingService.trackClick(ctx);
    node.addEventListener('click', handleClick);
    return () => node.removeEventListener('click', handleClick);
  }, [ctx]);

  return { ref };
};
